import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Button,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Storage,
  QueryStats,
  CloudQueue,
  Close,
  Security
} from '@mui/icons-material';
import { useEntityContext } from '../EntityContextSwitch';
import api from '../../services/api';
import GCSDetailView from './GCSDetailView';
import BigQueryDetailView from './BigQueryDetailView';
import FirestoreDetailView from './FirestoreDetailView';

const dataSourceTiles = [
  {
    id: 'gcs',
    title: 'Google Cloud Storage',
    description: 'Files and objects stored in GCS buckets',
    icon: <Storage sx={{ fontSize: 60 }} />,
    color: '#4285F4',
    component: GCSDetailView
  },
  {
    id: 'bigquery',
    title: 'BigQuery',
    description: 'Structured data in BigQuery datasets and tables',
    icon: <QueryStats sx={{ fontSize: 60 }} />,
    color: '#EA4335',
    component: BigQueryDetailView
  },
  {
    id: 'firestore',
    title: 'Firestore',
    description: 'Document data stored in Firestore collections',
    icon: <CloudQueue sx={{ fontSize: 60 }} />,
    color: '#34A853',
    component: FirestoreDetailView
  }
];

// eslint-disable-next-line max-lines-per-function
function DataSources() {
  const [selectedSource, setSelectedSource] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tokenizing, setTokenizing] = useState(false);
  const [tokenizeResult, setTokenizeResult] = useState(null);
  const { currentEntity } = useEntityContext();
  const selectedEntityId = currentEntity?.id;

  const handleTileClick = (source) => {
    setSelectedSource(source);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedSource(null);
  };

  const handleTokenizeDocuments = async () => {
    if (!selectedEntityId) {
      setTokenizeResult({ type: 'error', message: 'No entity selected' });
      return;
    }

    setTokenizing(true);
    setTokenizeResult(null);

    try {
      const response = await api.processEntityDocuments(selectedEntityId, false);

      if (response.success) {
        setTokenizeResult({
          type: 'success',
          message: `Successfully processed ${response.processedDocuments} documents. ${response.skippedDocuments} documents were skipped.`,
          details: response
        });
      } else {
        setTokenizeResult({
          type: 'error',
          message: response.error || 'Tokenization failed'
        });
      }
    } catch (error) {
      console.error('Tokenization error:', error);
      setTokenizeResult({
        type: 'error',
        message: 'Failed to start tokenization process'
      });
    } finally {
      setTokenizing(false);
    }
  };

  const SelectedComponent = selectedSource?.component;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body1" color="text.secondary">
          Select a data source to view detailed information about the entity's data.
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={tokenizing ? <CircularProgress size={20} color="inherit" /> : <Security />}
          onClick={handleTokenizeDocuments}
          disabled={tokenizing || !selectedEntityId}
          sx={{ minWidth: 160 }}
        >
          {tokenizing ? 'Tokenizing...' : 'Tokenize Documents'}
        </Button>
      </Box>
      {tokenizeResult && (
        <Alert
          severity={tokenizeResult.type}
          sx={{ mb: 3 }}
          onClose={() => setTokenizeResult(null)}
        >
          {tokenizeResult.message}
          {tokenizeResult.details && (
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Processing ID: {tokenizeResult.details.processingId}
            </Typography>
          )}
        </Alert>
      )}
      <Grid container spacing={3}>
        {dataSourceTiles.map((source) => (
          <Grid
            key={source.id}
            size={{
              xs: 12,
              md: 4
            }}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}
            >
              <CardActionArea
                onClick={() => handleTileClick(source)}
                sx={{ height: '100%' }}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Box sx={{ color: source.color, mb: 2 }}>
                    {source.icon}
                  </Box>
                  <Typography variant="h5" gutterBottom>
                    {source.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {source.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
      {/* Detail Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { height: '90vh' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: selectedSource?.color }}>
              {selectedSource?.icon && React.cloneElement(selectedSource.icon, { fontSize: 'medium' })}
            </Box>
            <Typography variant="h6">
              {selectedSource?.title}
            </Typography>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {SelectedComponent && (
            <SelectedComponent />
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}

export default DataSources;